import { useState } from 'react';
import styles from './BarChart.module.css';

export type ChartPoint = {
  date: string;
  activateCount: number;
  recycleCount: number;
};

const WIDTH = 900;
const HEIGHT = 345;
const PAD_LEFT = 70;
const PAD_RIGHT = 40;
const PAD_TOP = 30;
const PAD_BOTTOM = 45;
const PLOT_W = WIDTH - PAD_LEFT - PAD_RIGHT;
const PLOT_H = HEIGHT - PAD_TOP - PAD_BOTTOM;
const BASE_Y = PAD_TOP + PLOT_H;
const GRID_LINES = 4;

const NICE_STEPS = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];

const niceMax = (value: number) => {
  if (value <= 0) {
    return 100;
  }
  const pow = Math.pow(10, Math.floor(Math.log10(value)));
  const n = value / pow;
  const step = NICE_STEPS.find((s) => n <= s) ?? 10;
  return step * pow;
};

const buildSmoothPath = (pts: { x: number; y: number }[]) => {
  if (pts.length === 0) {
    return '';
  }
  if (pts.length === 1) {
    return `M ${pts[0].x} ${pts[0].y}`;
  }
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
};

export const BarChart = ({ points, isHourly }: { points: ChartPoint[]; isHourly: boolean }) => {
  const [selected, setSelected] = useState<number | null>(null);
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');

  const rawMax = Math.max(1, ...points.map((p) => Math.max(p.activateCount, p.recycleCount)));
  const maxVal = niceMax(rawMax * 1.5);

  const n = points.length;
  const fmtLabel = (date: string) => isHourly ? date.slice(11, 16) : date.slice(5);

  const slot = PLOT_W / Math.max(n - 1, 1);
  const xOf = (i: number) => n <= 1 ? PAD_LEFT + PLOT_W / 2 : PAD_LEFT + slot * i;
  const yOf = (v: number) => BASE_Y - v / maxVal * PLOT_H;

  const barSlot = PLOT_W / Math.max(n, 1);
  const barCenter = (i: number) => PAD_LEFT + barSlot * i + barSlot / 2;
  const barW = Math.min(14, Math.max(4, barSlot * 0.3));
  const barGap = Math.min(4, barSlot * 0.1);

  const labelStep = Math.max(1, Math.ceil(n / 9));

  const sel = selected !== null ? points[selected] : null;
  let accA = 0;
  let accR = 0;
  const cumA = points.map((p) => accA += Number(p.activateCount) || 0);
  const cumR = points.map((p) => accR += Number(p.recycleCount) || 0);

  const gridValues = Array.from({ length: GRID_LINES + 1 }, (_, i) => maxVal / GRID_LINES * i);

  const activePts = points.map((p, i) => ({ x: xOf(i), y: yOf(Number(p.activateCount) || 0) }));
  const recyclePts = points.map((p, i) => ({ x: xOf(i), y: yOf(Number(p.recycleCount) || 0) }));

  const activeLine = buildSmoothPath(activePts);
  const recycleLine = buildSmoothPath(recyclePts);
  const areaBottom = BASE_Y;
  const activeArea =
    activeLine && `${activeLine} L ${activePts[activePts.length - 1].x} ${areaBottom} L ${activePts[0].x} ${areaBottom} Z`;
  const recycleArea =
    recycleLine && `${recycleLine} L ${recyclePts[recyclePts.length - 1].x} ${areaBottom} L ${recyclePts[0].x} ${areaBottom} Z`;

  const handleSelect = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = WIDTH / rect.width;
    const svgX = (e.clientX - rect.left) * ratio;
    if (n === 0) {
      return;
    }
    const idx = Math.round((svgX - PAD_LEFT) / Math.max(slot, 1));
    const clamped = Math.min(Math.max(idx, 0), n - 1);
    setSelected(selected === clamped ? null : clamped);
  };

  return (
    <div className={styles.chartWrap}>
      <div className={styles.tip}>
        <div className={styles.switch}>
          <button
            type="button"
            className={`${styles.switchBtn} ${chartType === 'line' ? styles.switchActive : ''}`}
            onClick={() => setChartType('line')}
            aria-label="曲线图"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 17l5-6 4 3 6-8" />
              <path d="M3 21h18" />
            </svg>
          </button>
          <button
            type="button"
            className={`${styles.switchBtn} ${chartType === 'bar' ? styles.switchActive : ''}`}
            onClick={() => setChartType('bar')}
            aria-label="柱状图"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 20V10M12 20V4M19 20v-7" />
              <path d="M3 21h18" />
            </svg>
          </button>
        </div>
        <div className={styles.tipMain}>
          {sel && <span className={styles.tipDate}>{fmtLabel(sel.date)}</span>}
          <span className={styles.tipItem}>
            <i className={styles.dotA} />
            激活 {Number(sel?.activateCount ?? 0).toLocaleString()}
          </span>
          <span className={styles.tipItem}>
            <i className={styles.dotR} />
            回收 {Number(sel?.recycleCount ?? 0).toLocaleString()}
          </span>
        </div>
        <div className={styles.tipSub}>
          <span className={styles.tipItem}>
            <i className={styles.dotA} />
            累计激活 {sel ? cumA[selected!].toLocaleString() : 0}
          </span>
          <span className={styles.tipItem}>
            <i className={styles.dotR} />
            累计回收 {sel ? cumR[selected!].toLocaleString() : 0}
          </span>
        </div>
      </div>

      <div className={styles.svgWrap}>
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          preserveAspectRatio="xMidYMid meet"
          onClick={handleSelect}
        >
          <defs>
            <linearGradient id="activeLineGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#4C9AFF" />
              <stop offset="100%" stopColor="#1769FF" />
            </linearGradient>
            <linearGradient id="recoverLineGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#A98BFF" />
              <stop offset="100%" stopColor="#7352ED" />
            </linearGradient>
            <linearGradient id="activeAreaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(40,120,239,0.28)" />
              <stop offset="100%" stopColor="rgba(40,120,239,0)" />
            </linearGradient>
            <linearGradient id="recoverAreaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(115,82,237,0.22)" />
              <stop offset="100%" stopColor="rgba(115,82,237,0)" />
            </linearGradient>
          </defs>

          {gridValues.map((v) => {
            const y = yOf(v);
            return (
              <g key={v}>
                <line className={styles.grid} x1={PAD_LEFT} y1={y} x2={WIDTH - PAD_RIGHT} y2={y} />
                <text className={styles.axisText} x={PAD_LEFT - 12} y={y + 4} textAnchor="end">
                  {Math.round(v)}
                </text>
              </g>
            );
          })}
          <line className={styles.axis} x1={PAD_LEFT} y1={PAD_TOP} x2={PAD_LEFT} y2={BASE_Y} />
          <line className={styles.axis} x1={PAD_LEFT} y1={BASE_Y} x2={WIDTH - PAD_RIGHT} y2={BASE_Y} />

          {chartType === 'line' ? (
            <>
              <path d={recycleArea} fill="url(#recoverAreaGradient)" />
              <path d={activeArea} fill="url(#activeAreaGradient)" />
              <path
                d={recycleLine}
                fill="none"
                stroke="url(#recoverLineGradient)"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d={activeLine}
                fill="none"
                stroke="url(#activeLineGradient)"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {selected !== null && (
                <line
                  className={styles.cursor}
                  x1={xOf(selected)}
                  y1={PAD_TOP}
                  x2={xOf(selected)}
                  y2={BASE_Y}
                />
              )}
              {points.map((p, i) => {
                const isSel = selected === i;
                const showDot = isSel || n <= 15;
                if (!showDot) {
                  return null;
                }
                return (
                  <g key={p.date}>
                    <circle
                      cx={xOf(i)}
                      cy={yOf(Number(p.activateCount) || 0)}
                      r={isSel ? 5 : 3}
                      fill="#fff"
                      stroke="#1769FF"
                      strokeWidth={2.5}
                    />
                    <circle
                      cx={xOf(i)}
                      cy={yOf(Number(p.recycleCount) || 0)}
                      r={isSel ? 5 : 3}
                      fill="#fff"
                      stroke="#7352ED"
                      strokeWidth={2.5}
                    />
                  </g>
                );
              })}
              {points.map((p, i) =>
                i % labelStep === 0 ? (
                  <text
                    key={`label-${p.date}`}
                    className={styles.axisText}
                    x={xOf(i)}
                    y={BASE_Y + 24}
                    textAnchor="middle"
                  >
                    {fmtLabel(p.date)}
                  </text>
                ) : null
              )}
            </>
          ) : (
            <>
              {points.map((p, i) => {
                const a = Number(p.activateCount) || 0;
                const r = Number(p.recycleCount) || 0;
                const cx = barCenter(i);
                const groupW = barW * 2 + barGap;
                const aX = cx - groupW / 2;
                const rX = aX + barW + barGap;
                const aH = a / maxVal * PLOT_H;
                const rH = r / maxVal * PLOT_H;
                return (
                  <g
                    key={p.date}
                    className={styles.barGroup}
                    onClick={() => setSelected(selected === i ? null : i)}
                  >
                    <rect
                      x={cx - barSlot / 2}
                      y={PAD_TOP}
                      width={barSlot}
                      height={PLOT_H}
                      fill={selected === i ? 'rgba(40,120,239,0.06)' : 'transparent'}
                    />
                    <rect
                      x={aX}
                      y={BASE_Y - aH}
                      width={barW}
                      height={Math.max(aH, 0)}
                      rx={4}
                      fill="url(#activeLineGradient)"
                    />
                    <rect
                      x={rX}
                      y={BASE_Y - rH}
                      width={barW}
                      height={Math.max(rH, 0)}
                      rx={4}
                      fill="url(#recoverLineGradient)"
                    />
                  </g>
                );
              })}
              {points.map((p, i) =>
                i % labelStep === 0 ? (
                  <text
                    key={`label-${p.date}`}
                    className={styles.axisText}
                    x={barCenter(i)}
                    y={BASE_Y + 24}
                    textAnchor="middle"
                  >
                    {fmtLabel(p.date)}
                  </text>
                ) : null
              )}
            </>
          )}
        </svg>
      </div>

      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <i className={styles.dotA} />
          激活
        </span>
        <span className={styles.legendItem}>
          <i className={styles.dotR} />
          回收
        </span>
      </div>
    </div>
  );
};
